/**
 * ContactDialog — inbound "Contact us" form for the Hospital / Enterprise
 * tier CTAs on the pricing page.
 *
 * Posts to /v1/contact-requests (public endpoint with rate limiting).
 * No auth required. Displays a "we'll be in touch within 24 hours"
 * confirmation on success.
 *
 * Lightweight: uses native fetch, no api-client dependency. Closeable via
 * X button, Escape key, or click outside.
 */
import { useEffect, useRef, useState, FormEvent } from "react"

type Props = {
  open:           boolean
  onClose:        () => void
  /** Pre-fills the tier_interest field on the API record. */
  tierInterest?:  string                 // e.g. "hospital" | "enterprise"
  /** What the form heading reads — overrides default. */
  title?:         string
  /** Sub-headline under the title. */
  subtitle?:      string
}

export default function ContactDialog({
  open, onClose, tierInterest, title, subtitle,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted,  setSubmitted]  = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  // Close on Escape and outside-click.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose() }
    function onClick(e: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onClick)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onClick)
      document.body.style.overflow = ""
    }
  }, [open, onClose])

  // Reset state every time the dialog opens fresh.
  useEffect(() => {
    if (open) {
      setSubmitted(false)
      setError(null)
    }
  }, [open])

  if (!open) return null

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    const fd      = new FormData(e.currentTarget)
    const payload = {
      name:                fd.get("name"),
      organization:        fd.get("organization") || undefined,
      role:                fd.get("role")         || undefined,
      email:               fd.get("email"),
      phone:               fd.get("phone")        || undefined,
      preferred_call_time: fd.get("preferred_call_time") || undefined,
      message:             fd.get("message")      || undefined,
      source_page:         typeof window !== "undefined" ? window.location.href : undefined,
      tier_interest:       tierInterest,
    }

    try {
      // Resolve the API base. snowmed.health (marketing landing) doesn't proxy
      // /api → backend, so we need an absolute URL there. Other hosts (the
      // logged-in app, *.summary.to) already proxy correctly via VITE_API_URL.
      const envBase = import.meta.env.VITE_API_URL as string | undefined
      const host    = typeof window !== "undefined" ? window.location.hostname : ""
      const isMarketingHost = host === "snowmed.health" || host === "www.snowmed.health"
      const apiBase = envBase
        ?? (isMarketingHost ? "https://api.snowmed.io" : "/api")
      const res = await fetch(`${apiBase}/v1/contact-requests`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (res.status === 429) {
          setError("You've sent a few requests recently. Please try again in 10 minutes.")
        } else if (data.error === "invalid_email") {
          setError("That email address doesn't look right.")
        } else if (data.error === "service_not_ready") {
          setError("Our contact system is being set up. Please email hello@snowmed.io directly.")
        } else {
          setError(data.error ?? "Something went wrong. Please try again.")
        }
        setSubmitting(false)
        return
      }
      setSubmitted(true)
    } catch {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-dialog-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-8"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-xl bg-white shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between p-6 pb-2">
          <div>
            <h2 id="contact-dialog-title" className="text-xl font-bold text-gray-900">
              {title ?? "Talk to us"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {subtitle ?? "Tell us a bit about your team — we'll get back within 24 hours."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-700 transition-colors p-1"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {submitted ? (
          <div className="p-6 pt-2 space-y-4 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Got it.</h3>
              <p className="text-sm text-gray-500 mt-1">
                We'll reach out within 24 hours. If it's urgent, email{" "}
                <a className="underline" href="mailto:hello@snowmed.io">hello@snowmed.io</a>.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-4">
            <Field name="name" label="Your name" required autoComplete="name" />
            <div className="grid grid-cols-2 gap-3">
              <Field name="organization" label="Organization" placeholder="Hospital / clinic / company" />
              <Field name="role"         label="Your role"    placeholder="Doctor / admin / dev" />
            </div>
            <Field name="email" label="Email" type="email" required autoComplete="email" />
            <div className="grid grid-cols-2 gap-3">
              <Field name="phone" label="Phone" type="tel" placeholder="+91 …" autoComplete="tel" />
              <Field name="preferred_call_time" label="Best time to call" placeholder="e.g. weekday afternoons" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">What can we help with?</label>
              <textarea
                name="message"
                rows={3}
                placeholder="A few lines about what you're looking to solve…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-400">
                By submitting, you agree to be contacted by SNOW.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 rounded-md bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function Field({
  name, label, type = "text", required, placeholder, autoComplete,
}: {
  name:         string
  label:        string
  type?:        string
  required?:    boolean
  placeholder?: string
  autoComplete?: string
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
      />
    </div>
  )
}
